import type { MenuRootProps } from '@chakra-ui/react'
import { HStack, Spacer, Stack } from '@chakra-ui/react'
import type { CombinedError } from '@urql/core'
import type { FC, FormEventHandler } from 'react'
import { useCallback } from 'react'
import { useState } from 'react'

import type { ButtonProps } from '@app/components/ui/button'
import { Button } from '@app/components/ui/button'
import { Field } from '@app/components/ui/field'
import { MenuContent, MenuItem, MenuRoot, MenuTrigger } from '@app/components/ui/menu'
import { NativeSelectField, NativeSelectRoot } from '@app/components/ui/native-select'
import { PopoverBody, PopoverContent, PopoverRoot, PopoverTrigger } from '@app/components/ui/popover'
import { toaster } from '@app/components/ui/toaster'
import DurationInput from '@app/next/forms/durationInput'
import unpackErrors from '@app/utils/unpackErrors'
import type { StrategySimulationPopulateBaselinesMutationVariables } from '@graphql/queries'
import {
  SimulationBaselineSourceEnum,
  SimulationBaselineTimeBaseEnum,
  useStrategySimulationPopulateBaselinesMutation
} from '@graphql/queries'

type Props = ButtonProps & {
  strategyId: string
}

const errorToast = (error: CombinedError) => {
  const unpacked = unpackErrors([error])
  const message = unpacked.global?.message || 'Unknown error'

  toaster.error({ title: 'Error generating baselines', description: message, duration: 8000 })
}

const HistoryPopover = ({ populate, strategyId, open, setOpen, loading }) => {
  const onInteractOutside = useCallback(async () => {
    setOpen(false)
  }, [setOpen])

  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    async (e) => {
      e.preventDefault()

      const formData = new FormData(e.currentTarget)

      try {
        await populate({
          strategyId,
          source: SimulationBaselineSourceEnum.History,
          ...Object.fromEntries(formData.entries())
        })
      } finally {
        setOpen(false)
      }
    },
    [populate, strategyId, setOpen]
  )

  return (
    <PopoverRoot open={open} lazyMount onInteractOutside={onInteractOutside} unmountOnExit>
      <PopoverTrigger />
      <PopoverContent>
        <PopoverBody>
          <form onSubmit={onSubmit}>
            <Stack>
              <Field>
                <DurationInput name="range" defaultValue="P7D" />
              </Field>
              <Field label="Set baselines based on...">
                <NativeSelectRoot flex="30">
                  <NativeSelectField placeholder="" name="timeBase">
                    <option value={SimulationBaselineTimeBaseEnum.MostRecent}>Most recent period</option>
                    <option value={SimulationBaselineTimeBaseEnum.PreviousYear}>Previous year</option>
                  </NativeSelectField>
                </NativeSelectRoot>
              </Field>

              <HStack>
                <Spacer />
                <Button type="submit" w="fit-content" loading={loading}>
                  Go
                </Button>
              </HStack>
            </Stack>
          </form>
        </PopoverBody>
      </PopoverContent>
    </PopoverRoot>
  )
}

const MapSimulationPopulateBaselines: FC<Props> = ({ strategyId, ...props }) => {
  const [loading, setLoading] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [, run] = useStrategySimulationPopulateBaselinesMutation()

  const populate = useCallback(
    async (input: StrategySimulationPopulateBaselinesMutationVariables['input']) => {
      setLoading(true)
      try {
        const r = await run({ input })
        if (r.error) {
          errorToast(r.error)
        }
      } catch (e) {
        errorToast(e)
      } finally {
        setLoading(false)
      }
    },
    [run]
  )

  const onSelect: MenuRootProps['onSelect'] = useCallback(
    async ({ value }) => {
      if (value === SimulationBaselineSourceEnum.Ai) {
        await populate({ strategyId, source: value })
      } else if (value === 'history') {
        setHistoryOpen(true)
      }
    },
    [populate, strategyId]
  )

  return (
    <>
      <MenuRoot onSelect={onSelect}>
        <MenuTrigger asChild>
          <Button loading={loading} {...props}>
            Populate baselines
          </Button>
        </MenuTrigger>
        <MenuContent>
          <MenuItem value="ai">With AI</MenuItem>
          <MenuItem value="history">With historical data</MenuItem>
        </MenuContent>
      </MenuRoot>
      <HistoryPopover
        populate={populate}
        strategyId={strategyId}
        open={historyOpen}
        setOpen={setHistoryOpen}
        loading={loading}
      />
    </>
  )
}

export default MapSimulationPopulateBaselines
